/**
 * @description Niveaux de risque personnalisé.
 *
 * - UNDEFINED: Risque non défini (gris).
 * - LOW: Risque faible (vert).
 * - MEDIUM: Risque standard (orange).
 * - HIGH: Risque élevé (rouge).
 * @remarks Contrairement au RiskLevel du backend, on n'utilise pas HIGH et CRITICAL.
 * @see {@link getRiskLevelValue} pour la conversion en nombre (pour faire des comparaisons).
 * @see {@link getRiskClass} pour la classe CSS associée (pour l'affichage).
 * @see {@link mapRiskLevelToCustomRiskLevel} pour la conversion de RiskLevel en CustomRiskLevel.
 * @see {@link mapCustomRiskLevelToRiskLevel} pour la conversion de CustomRiskLevel en RiskLevel.
 */
export enum CustomRiskLevel {
  UNDEFINED = "UNDEFINED",
  LOW = "LOW",
  NORMAL = "NORMAL",
  MEDIUM = "MEDIUM", // Note: "NORMAL" is used in the code, but "MEDIUM" is more appropriate for risk levels.
  HIGH = "HIGH",
  IN_PROGRESS = "IN_PROGRESS",
}

export const RISK_LEVEL_CLASSES = {
  [CustomRiskLevel.UNDEFINED]: "bg-muted",
  [CustomRiskLevel.LOW]: "bg-success",
  [CustomRiskLevel.NORMAL]: "bg-orange-medium",
  [CustomRiskLevel.MEDIUM]: "bg-orange-medium",
  [CustomRiskLevel.HIGH]: "bg-alert",
  [CustomRiskLevel.IN_PROGRESS]: "bg-blue-medium",
} as const;

export const RISK_LEVEL_ICON_CLASSES = {
  [CustomRiskLevel.UNDEFINED]: "text-muted",
  [CustomRiskLevel.LOW]: "text-success",
  [CustomRiskLevel.MEDIUM]: "text-orange-medium",
  [CustomRiskLevel.NORMAL]: "text-orange-medium",
  [CustomRiskLevel.HIGH]: "text-alert",
  [CustomRiskLevel.IN_PROGRESS]: "text-blue-completed",
} as const;

export const getRiskLevelValue = (level: CustomRiskLevel): number => {
  switch (level) {
    case "HIGH":
      return 3;
    case "MEDIUM":
      return 2;
    case "NORMAL":
      return 2; // "NORMAL" is treated the same as "MEDIUM" for
    case "LOW":
      return 1;
    case "UNDEFINED":
    default:
      return 0;
  }
};

/**
 * @description Les axes de risque utilisés au niveau de la liste des dossiers sensibles.
 * - Client : Risque au niveau du client (contient les sous-risques de sanction et de PEP)
 * - Localisation : Risque au niveau de la localisation du client
 * - Sanction : Risque au niveau de la sanction du client
 * - PEP : Risque au niveau du PEP du client
 */
export enum CustomRiskAxis {
  CLIENT = "Client",
  LOCALISATION = "Localisation",
  MISSIONS = "Missions",
  ACTIVITE = "Activité",
  SANCTION = "Sanction",
  PEP = "PEP",
}

export const RiskLevelColorMap: Record<CustomRiskLevel, string> = {
  [CustomRiskLevel.HIGH]: "#FE6447",
  [CustomRiskLevel.MEDIUM]: "#F3AC60",
  [CustomRiskLevel.NORMAL]: "#F3AC60",
  [CustomRiskLevel.LOW]: "#00D6A8",
  [CustomRiskLevel.UNDEFINED]: "#F6F7FA",
  [CustomRiskLevel.IN_PROGRESS]: "#00A8E4",
};
